import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {AlertCircle, Headphones, MicOff, MonitorUp, VideoOff, VolumeX} from 'lucide-react';
import {useAudioPlayback, useMaybeRoomContext} from '@livekit/components-react';
import type {LocalDeviceIssues} from '@phonehq/rtc-core';
import {loadDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {CallToast} from '../components/CallToast';
import {groupActions} from './groupActions';
import {groupSurfaceMessages as m} from './groupMessages';
import {CallLayer} from '../components/CallLayer';

const SWITCHED_MS = 6_000;
const CONFLICT_MS = 5_000;

const SoundToast = () => {
    const intl = useIntl();
    const room = useMaybeRoomContext();
    const {canPlayAudio, startAudio} = useAudioPlayback(room);
    if (canPlayAudio) {
        return null;
    }
    return (
        <CallToast
            testId="group-call-enable-audio"
            icon={<VolumeX size={18} aria-hidden="true"/>}
            lead={intl.formatMessage(m.soundPausedLead)}
            body={intl.formatMessage(m.soundPausedBody)}
            actionLabel={intl.formatMessage(m.turnOnSound)}
            actionTone="brand"
            onAction={() => void startAudio()}
        />
    );
};

const useDeviceSwitched = (): boolean => {
    const [switched, setSwitched] = useState(false);
    useEffect(() => {
        const media = navigator.mediaDevices;
        if (!media?.addEventListener) {
            return;
        }
        let timer: number | undefined;
        const onChange = async () => {
            const preferred = loadDevicePreferences();
            const wanted = [preferred.audioInputId, preferred.audioOutputId].filter((id): id is string => !!id);
            if (wanted.length === 0) {
                return;
            }
            const present = new Set((await media.enumerateDevices()).map(device => device.deviceId));
            if (wanted.some(id => !present.has(id))) {
                setSwitched(true);
                window.clearTimeout(timer);
                timer = window.setTimeout(() => setSwitched(false), SWITCHED_MS);
            }
        };
        media.addEventListener('devicechange', onChange);
        return () => {
            media.removeEventListener('devicechange', onChange);
            window.clearTimeout(timer);
        };
    }, []);
    return switched;
};

interface GroupToastsProps {
    readonly issues?: LocalDeviceIssues;
    readonly conflictWith?: string;
    readonly onConflictShown: () => void;
    readonly onHowToAllow: (kind: 'microphone' | 'camera') => void;
    readonly onOpenDevices: () => void;
}

export const GroupToasts = ({issues, conflictWith, onConflictShown, onHowToAllow, onOpenDevices}: GroupToastsProps) => {
    const intl = useIntl();
    const room = useMaybeRoomContext();
    const switched = useDeviceSwitched();
    const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        if (!conflictWith) {
            return;
        }
        const timer = window.setTimeout(onConflictShown, CONFLICT_MS);
        return () => window.clearTimeout(timer);
    }, [conflictWith, onConflictShown]);

    const dismiss = (key: string) => setDismissed(current => new Set(current).add(key));
    const micKey = issues?.microphone ? `microphone:${issues.microphone}` : undefined;
    const cameraKey = issues?.camera ? `camera:${issues.camera}` : undefined;

    const microphoneToast = micKey && !dismissed.has(micKey) && (() => {
        switch (issues?.microphone) {
            case 'blocked':
                return <CallToast key={micKey} icon={<MicOff size={18} className="text-[var(--phq-call-danger-soft)]" aria-hidden="true"/>} lead={intl.formatMessage(m.micBlockedLead)} body={intl.formatMessage(m.micBlockedBody)} actionLabel={intl.formatMessage(m.howToAllow)} onAction={() => onHowToAllow('microphone')}/>;
            case 'not_found':
                return <CallToast key={micKey} icon={<MicOff size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>} lead={intl.formatMessage(m.micMissingLead)} body={intl.formatMessage(m.micMissingBody)} actionLabel={intl.formatMessage(m.captionDevices)} onAction={onOpenDevices}/>;
            default:
                return <CallToast key={micKey} icon={<AlertCircle size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>} lead={intl.formatMessage(m.micBusyLead)} body={intl.formatMessage(m.deviceBusyBody)} actionLabel={intl.formatMessage(m.tryAgain)} onAction={() => { dismiss(micKey); groupActions.setMicrophone(true); }}/>;
        }
    })();

    const cameraToast = cameraKey && !dismissed.has(cameraKey) && (() => {
        switch (issues?.camera) {
            case 'blocked':
                return <CallToast key={cameraKey} icon={<VideoOff size={18} className="text-[var(--phq-call-danger-soft)]" aria-hidden="true"/>} lead={intl.formatMessage(m.cameraBlockedLead)} body={intl.formatMessage(m.cameraBlockedBody)} actionLabel={intl.formatMessage(m.howToAllow)} onAction={() => onHowToAllow('camera')}/>;
            case 'not_found':
                return <CallToast key={cameraKey} icon={<VideoOff size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>} lead={intl.formatMessage(m.cameraMissingLead)} body={intl.formatMessage(m.cameraMissingBody)} actionLabel={intl.formatMessage(m.captionDevices)} onAction={() => { dismiss(cameraKey); onOpenDevices(); }}/>;
            default:
                return <CallToast key={cameraKey} icon={<AlertCircle size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>} lead={intl.formatMessage(m.cameraBusyLead)} body={intl.formatMessage(m.deviceBusyBody)} actionLabel={intl.formatMessage(m.tryAgain)} onAction={() => { dismiss(cameraKey); groupActions.setCamera(true); }}/>;
        }
    })();

    return createPortal(
        <CallLayer>{
        <div className="phq-call-ui pointer-events-none fixed left-1/2 top-3 z-[70] flex w-[min(640px,calc(100vw-32px))] -translate-x-1/2 flex-col items-center gap-2">
            {room && <SoundToast/>}
            {microphoneToast}
            {cameraToast}
            {switched && (
                <CallToast icon={<Headphones size={18} className="text-[var(--phq-call-brand-focus)]" aria-hidden="true"/>} lead={intl.formatMessage(m.switchedLead)} body={intl.formatMessage(m.switchedBody)} actionLabel={intl.formatMessage(m.captionDevices)} onAction={onOpenDevices}/>
            )}
            {conflictWith && (
                <CallToast icon={<MonitorUp size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>} lead={intl.formatMessage(m.shareConflict, {name: conflictWith})}/>
            )}
        </div>}</CallLayer>,
        document.body,
    );
};
